import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import authInstance from '../../services/httpClient';
import { stagingApiBase } from './stagingOrg';

// Tags-tab changes reach live products at once (Jason, 28 Oct 2026). Before one is saved, a
// small alert says how many live products, pending and flagged items it will re-tag. A change
// that touches nothing saves without asking; a preview that fails lets the save report the error.

export type TagChange =
  | { kind: 'reapply' }
  | { kind: 'role'; tag: string; role: 'chip' | 'ignore' | 'exclude' }
  | { kind: 'option'; name: string; enabled: boolean }
  | { kind: 'display'; tag: string; displayAs: string | null }
  | { kind: 'name_rule'; id?: number; rule: Record<string, unknown> | null };

export type ConfirmTagChange = (change: TagChange) => Promise<boolean>;

// exclude: the preview counts what Exclude hides / withdraws, and the question is always asked
interface Effect { live: number; pending: number; flagged: number; exclude?: boolean; tag?: string; unknown?: boolean }

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "41 live products, 3 pending items and 1 flagged item" (only the non-zero parts). */
export function effectText(e: Effect): string {
  const parts = [
    e.live ? count(e.live, 'live product', 'live products') : '',
    e.pending ? count(e.pending, 'pending item', 'pending items') : '',
    e.flagged ? count(e.flagged, 'flagged item', 'flagged items') : '',
  ].filter(Boolean);
  return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** confirm(change) resolves true to go ahead; render `dialog` once in the page. */
export function useTagChangeConfirm(): { confirm: ConfirmTagChange; dialog: React.ReactElement } {
  const [effect, setEffect] = useState<Effect | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmTagChange>(async (change) => {
    let e: Effect;
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/tags/preview`, change);
      e = { live: data?.live ?? 0, pending: data?.pending ?? 0, flagged: data?.flagged ?? 0, exclude: data?.exclude === true };
      if (e.exclude && change.kind === 'role') e.tag = change.tag;
    } catch {
      // Exclude hides live products: never without asking, even with no counts to show
      if (change.kind !== 'role' || change.role !== 'exclude') return true;
      e = { live: 0, pending: 0, flagged: 0, exclude: true, tag: change.tag, unknown: true };
    }
    if (!e.exclude && e.live + e.pending + e.flagged === 0) return true;
    // only one question at a time: an earlier open one is answered "no"
    resolver.current?.(false);
    setEffect(e);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  // leaving the page with a question open answers it "no"
  useEffect(() => () => { resolver.current?.(false); resolver.current = null; }, []);

  const close = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setEffect(null);
  };

  const dialog = (
    <Dialog open={effect !== null} onClose={() => close(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{effect?.exclude ? <>Exclude &ldquo;{effect.tag}&rdquo;?</> : 'Apply this change?'}</DialogTitle>
      <DialogContent>
        {effect?.exclude ? (
          <Typography variant="body2">
            {effect.unknown && 'Live products carrying this tag will be hidden from the app and items carrying it withdrawn (the counts could not be loaded). '}
            {!effect.unknown && (effect.live
              ? <>{count(effect.live, 'live product', 'live products')} carrying this tag will be hidden from the app. </>
              : 'No live product carries this tag. ')}
            {effect.pending + effect.flagged
              ? <>{effectText({ live: 0, pending: effect.pending, flagged: effect.flagged })} will be withdrawn to Excluded Products. </>
              : ''}
            New items carrying it arrive withdrawn. Setting the tag back to Chip or Ignore brings them all back.
          </Typography>
        ) : (
          <Typography variant="body2">
            This will update {effect ? effectText(effect) : ''}. Tags added by hand stay.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(false)}>Cancel</Button>
        <Button variant="contained" onClick={() => close(true)} autoFocus>{effect?.exclude ? 'Exclude' : 'Apply'}</Button>
      </DialogActions>
    </Dialog>
  );
  return { confirm, dialog };
}
